import React, { useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { createSelector, Dispatch } from '@reduxjs/toolkit'
import { createStructuredSelector } from 'reselect'
import { GridApi } from 'ag-grid-community'
import { isEmpty, isEqual } from 'lodash-es'
import { GridActions, GridToolbarSettings } from 'store/grid'
import { GridColumnCopySelection, GridId } from 'store/grid/gridTypes'
import { createSelectedGridSettingsSelector } from 'selectors/gridSelectors'
import { ColumnCopySelectorComponent } from './ColumnCopySelectorComponent'
import {
  getOriginalOrCurrentColumnId,
  columnCopySelectionsToGroupedColumnCopySelectorSelectionsMapper,
  useStableColumns,
  createSortColumns
} from './helpers'

import { DefaultColsByGridId } from './constants'

interface ColumnCopySelectorContainerProps {
  gridId: GridId
  gridApi: GridApi
  toolbarSettings: GridToolbarSettings
  gridColumnCopySelections: GridColumnCopySelection[]
  onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
  saveColumnCopySelections: (columnSelections: GridColumnCopySelection[]) => void
  defaultCopyColumnIds: string[]
}

export const ColumnCopySelectorContainer: React.FC<ColumnCopySelectorContainerProps> = props => {
  const {
    gridApi,
    toolbarSettings,
    onToolbarSettingsChanged,
    saveColumnCopySelections,
    gridColumnCopySelections: columnSelections,
    defaultCopyColumnIds
  } = props

  const columns = useStableColumns(gridApi, createSortColumns(defaultCopyColumnIds))

  useEffect(() => {
    if (!isEmpty(columns)) {
      const updatedSelections = columns.map<GridColumnCopySelection>(col => {
        const colId = col.getColId()
        const originalOrCurrentColumnId = getOriginalOrCurrentColumnId(col)
        const currentSelectionsByColumnId = columnSelections.find(s => s.columnId === colId)
        const currentSelectionsByGroupId = columnSelections.find(s => s.groupingId === originalOrCurrentColumnId)
        return {
          groupingId: originalOrCurrentColumnId,
          columnId: colId,
          isSelected:
            (currentSelectionsByColumnId?.isSelected || currentSelectionsByGroupId?.isSelected) ??
            (!['TARGET', 'DELTA', 'ORIGINAL'].includes(originalOrCurrentColumnId) &&
              defaultCopyColumnIds.includes(originalOrCurrentColumnId))
        }
      })

      if (!isEqual(updatedSelections, columnSelections)) {
        saveColumnCopySelections(updatedSelections)
      }
    }
  }, [columnSelections, columns, defaultCopyColumnIds, saveColumnCopySelections])

  const toggleSelectionSelected = useCallback(
    (groupedSelectionId: string) => {
      const selectionsToToggle = columnSelections.filter(selection => selection.groupingId === groupedSelectionId)

      if (selectionsToToggle.length) {
        saveColumnCopySelections(
          columnSelections.reduce(
            (acc, selection) => [
              ...acc,
              selectionsToToggle.includes(selection) ? { ...selection, isSelected: !selection.isSelected } : selection
            ],
            []
          )
        )
      }
    },
    [saveColumnCopySelections, columnSelections]
  )

  const handleCopy = useCallback(() => {
    const selectedRows = gridApi.getSelectedRows()
    if (selectedRows.length === 0) {
      return
    }
    const allColumnIds = columns.map(c => c.getColId())
    const selectedColumnIds = columnSelections.reduce(
      (acc, selection) => (selection.isSelected === true && allColumnIds.includes(selection.columnId) ? [...acc, selection.columnId] : acc),
      []
    )

    if (selectedColumnIds.length > 0) {
      gridApi.copySelectedRowsToClipboard({ columnKeys: selectedColumnIds, includeHeaders: true, includeGroupHeaders: true })
    }
  }, [columns, gridApi, columnSelections])

  const updateToolbarSettings = useCallback(
    (propName: keyof GridToolbarSettings) => {
      const updatedToolbarSettings: GridToolbarSettings = {
        ...toolbarSettings,
        [propName]: !toolbarSettings[propName]
      }
      onToolbarSettingsChanged(updatedToolbarSettings)
    },
    [onToolbarSettingsChanged, toolbarSettings]
  )

  const onToggleShowCopySelected = useCallback(() => {
    updateToolbarSettings('showCopySelected')
  }, [updateToolbarSettings])

  const selections = useMemo(() => columnCopySelectionsToGroupedColumnCopySelectorSelectionsMapper(columnSelections, columns), [
    columnSelections,
    columns
  ])

  return (
    <ColumnCopySelectorComponent
      onToggleShowCopySelected={onToggleShowCopySelected}
      toggleSelectionSelected={toggleSelectionSelected}
      handleCopy={handleCopy}
      selections={selections}
      toolbarSettings={toolbarSettings}
    />
  )
}

const mapStateToProps = createStructuredSelector({
  gridColumnCopySelections: createSelector(
    [
      createSelectedGridSettingsSelector(
        (_state: {}, ownProps: ColumnCopySelectorContainerProps) => ownProps.gridId,
        () => 'default'
      )
    ],
    settings => settings.columnCopySelections ?? []
  ),
  defaultCopyColumnIds: createSelector(
    (_state: {}, ownProps: ColumnCopySelectorContainerProps) => ownProps.gridId,
    gridId => DefaultColsByGridId[gridId]
  )
})

const mapDispatchToProps = (dispatch: Dispatch, ownProps: ColumnCopySelectorContainerProps) => ({
  saveColumnCopySelections: (selections: GridColumnCopySelection[]) =>
    dispatch(GridActions.saveGridColumnCopySelections(ownProps.gridId, 'default', selections))
})

export const ColumnCopySelector = connect(mapStateToProps, mapDispatchToProps)(ColumnCopySelectorContainer)
